/**
 * Purpose:
 *   Hook to manage Avatar dropdown state and behavior.
 *   Handles open/close state, click outside detection, and keyboard navigation.
 *
 * Notes:
 *   - Uses refs to track dropdown container for click-outside detection
 *   - Supports keyboard navigation (Escape to close)
 *   - Manages dropdown visibility with animation states
 */

'use client';

import { useState, useCallback, useRef, useEffect } from 'react';

export interface UseAvatarDropdownReturn {
  /** Whether the dropdown is currently open */
  isOpen: boolean;
  /** Reference to attach to the dropdown container */
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  /** Toggle dropdown open/close state */
  toggleDropdown: () => void;
  /** Close the dropdown */
  closeDropdown: () => void;
  /** CSS class for dropdown animation state */
  dropdownState: 'open' | 'closed';
}

export function useAvatarDropdown(): UseAvatarDropdownReturn {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, closeDropdown]);

  const dropdownState = isOpen ? 'open' : 'closed';

  return {
    isOpen,
    dropdownRef,
    toggleDropdown,
    closeDropdown,
    dropdownState,
  };
}

